/**
 * Pricing, as the code is allowed to know it.
 *
 * `docs/product/pricing.md` is the source of truth and says so in its own first
 * line: every price shown to a customer must match it, and if a number
 * disagrees with that file, the file is right and the other place is a bug. It
 * also says **"Do not paraphrase this file into another document. Link to it."**
 *
 * This module is not a paraphrase — it is the LOCKED subset, machine-readable,
 * so a screen can render a price without a person retyping one. Everything the
 * document marks PROPOSED or OPEN is deliberately **absent**, and
 * `UNAPPROVED_ALLOWANCES` records why, because the failure this file exists to
 * prevent is a plausible-looking number reaching a customer.
 *
 * The rule for anything added here: if the document does not say LOCKED, it
 * does not belong in this file.
 */

/** A public plan. `priceUsd: null` means "quote", not "free". */
export interface Plan {
  key: 'free' | 'home' | 'household' | 'private' | 'private_plus';
  name: string;
  /** Monthly, USD. `null` = custom quote. */
  priceUsd: number | null;
  /** True when the price is a floor rather than the price. */
  from: boolean;
  /** Worker seats included. `null` only for a contract-defined plan. */
  workers: number | null;
  /** Included cloud storage, in GB (1 GB = 1,000,000,000 bytes). */
  storageGb: number | null;
  /** One line, as the document words it. */
  profile: string;
}

/**
 * §1 of the document — **LOCKED**. Safe to ship, safe to show a customer.
 *
 * Prices are USD; mobile stores may localize the *displayed* currency, which is
 * a store concern and never a different price.
 */
export const PLANS: readonly Plan[] = [
  {
    key: 'free',
    name: 'Free',
    priceUsd: 0,
    from: false,
    workers: 1,
    storageGb: 0,
    profile: 'Simple household',
  },
  {
    key: 'home',
    name: 'Home',
    priceUsd: 39,
    from: false,
    workers: 1,
    storageGb: 10,
    profile: 'Small family, one domestic worker',
  },
  {
    key: 'household',
    name: 'Household',
    priceUsd: 99,
    from: false,
    workers: 3,
    storageGb: 25,
    profile: 'Established household, small staff',
  },
  {
    key: 'private',
    name: 'Private Household',
    priceUsd: 199,
    from: true,
    workers: 6,
    storageGb: 50,
    profile: 'Larger staffed household',
  },
  {
    key: 'private_plus',
    name: 'Private Plus',
    priceUsd: null,
    from: false,
    // Contract-defined, not a package limit.
    workers: null,
    storageGb: null,
    profile: 'Complex or multi-property',
  },
] as const;

/** The public currency. Stores may localize display; the price does not change. */
export const PRICING_CURRENCY = 'USD' as const;

/**
 * What the Free plan actually includes — §2, **LOCKED**.
 *
 * Written out because it is the single easiest thing to get wrong on a pricing
 * page, and it was already got wrong once: a pricing mockup promised
 * "unlimited standard translations" and "unlimited basic receipt OCR" as
 * *included in every plan*, while this list excludes both from Free outright.
 * Those two are per-use costs. A promise like that is not a wording slip, it is
 * an unbounded bill.
 */
export const FREE_PLAN = {
  includes: ['manual text task creation', 'basic task management'],
  excludes: [
    'AI actions',
    'automatic translation',
    'receipt scanning',
    'image uploads',
    'voice processing',
    'WhatsApp task management',
  ],
  /** English only. The worker-language rule starts at Home. */
  languages: 0,
} as const;

/**
 * Everything a customer-facing surface must NOT state yet.
 *
 * Exported as data rather than left as prose so a pricing screen can render an
 * honest "not yet announced" for each one instead of inventing a number, and so
 * a test can assert none of them leaked into `PLANS`.
 *
 * From §3 (PROPOSED — "Do not put these numbers in front of a customer yet")
 * and the OPEN register at the end of the document.
 */
export const UNAPPROVED_ALLOWANCES: readonly string[] = [
  'active-task and archive limits',
  'the Private quote formula above six workers',
  'billing channel, VAT display and local currency',
  'overage pricing past an AI, voice or receipt allowance',
  'the language-pack upgrade offer',
] as const;

/**
 * §3, LOCKED 2026-08-07. Monthly, per household.
 *
 * `whatsappRescueEvents` is deliberately not "messages". WhatsApp is a rescue
 * channel: the app notification and push go first, and one reminder follows
 * only if an important task was never opened. One event is an outbound utility
 * message plus the inbound acknowledgement. Restoring a working-channel model
 * voids the margin table in §4 of the document, and Home is the plan that
 * breaks first.
 */
export interface PlanAllowances {
  translations: number;
  aiActions: number;
  receiptScans: number;
  voiceMinutes: number;
  whatsappRescueEvents: number;
}

export const ALLOWANCES: Readonly<Record<Plan['key'], PlanAllowances | null>> = {
  free: {
    translations: 0,
    aiActions: 0,
    receiptScans: 0,
    voiceMinutes: 0,
    whatsappRescueEvents: 0,
  },
  home: {
    translations: 300,
    aiActions: 75,
    receiptScans: 20,
    voiceMinutes: 90,
    whatsappRescueEvents: 30,
  },
  household: {
    translations: 600,
    aiActions: 150,
    receiptScans: 50,
    voiceMinutes: 200,
    whatsappRescueEvents: 90,
  },
  private: {
    translations: 1200,
    aiActions: 300,
    receiptScans: 100,
    voiceMinutes: 400,
    whatsappRescueEvents: 180,
  },
  // Contract-defined; 30 rescue events per contracted worker.
  private_plus: null,
};

/** §6b, LOCKED. Capacity only — never AI, OCR, translation or workers. */
export interface StorageBox {
  capacityGb: number;
  monthlyUsd: number;
  /** Twelve months paid up front. Not a discount. */
  annualUsd: number;
}

export const STORAGE_BOXES: readonly StorageBox[] = [
  { capacityGb: 50, monthlyUsd: 3.99, annualUsd: 47.88 },
  { capacityGb: 100, monthlyUsd: 6.99, annualUsd: 83.88 },
  { capacityGb: 200, monthlyUsd: 11.99, annualUsd: 143.88 },
] as const;

/** Is this plan safe to render with a number next to it? */
export function hasPublicPrice(plan: Plan): boolean {
  return plan.priceUsd !== null;
}

/** How a plan's price should read on a surface. Never invents a number. */
export function priceLabel(plan: Plan): string {
  if (plan.priceUsd === null) return 'Custom quote';
  if (plan.priceUsd === 0) return '$0';
  return plan.from ? `from $${plan.priceUsd}` : `$${plan.priceUsd}`;
}
